import type { OrderItemStatus } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { DatePicker } from '../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Field } from '../ui/field';
import { Input } from '../ui/input';

export interface OrderItemStatusAction {
	label: string;
	status: OrderItemStatus;
	requiredFields: string[];
	optionalFields: string[];
}

/** Values collected from the action's required/optional fields, ready for the mutation input. */
export interface OrderItemStatusFields {
	trackingNumber?: string;
	carrier?: string;
	estimatedDeliveryDate?: string;
}

/** Field name → how to render it. Unknown fields fall back to a humanized text input. */
const FIELD_META: Record<string, { label: string; kind: 'text' | 'date'; placeholder?: string }> = {
	trackingNumber: {
		label: 'Tracking number',
		kind: 'text',
		placeholder: '1Z999AA10123456784',
	},
	carrier: { label: 'Carrier', kind: 'text', placeholder: 'UPS, DHL, FedEx…' },
	estimatedDeliveryDate: { label: 'Estimated delivery date', kind: 'date' },
};

function metaFor(field: string): { label: string; kind: 'text' | 'date'; placeholder?: string } {
	return (
		FIELD_META[field] ?? {
			label: field
				.replace(/([A-Z])/g, ' $1')
				.replace(/^./, (c) => c.toUpperCase())
				.trim(),
			kind: 'text',
		}
	);
}

export function UpdateOrderItemStatusModal({
	open,
	onOpenChange,
	action,
	productName,
	loading,
	onSubmit,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	action: OrderItemStatusAction | null;
	productName: string;
	loading: boolean;
	onSubmit: (fields: OrderItemStatusFields) => void;
}) {
	const [text, setText] = useState<Record<string, string>>({});
	const [dates, setDates] = useState<Record<string, Date | null>>({});

	// Reset whenever a new action is opened.
	useEffect(() => {
		if (open) {
			setText({});
			setDates({});
		}
	}, [open, action?.status]);

	if (!action) return null;

	const fields = [...action.requiredFields, ...action.optionalFields];
	const requiredSet = new Set(action.requiredFields);

	const hasValue = (field: string) =>
		metaFor(field).kind === 'date' ? !!dates[field] : !!text[field]?.trim();

	const missingRequired = action.requiredFields.filter((f) => !hasValue(f));

	const handleSubmit = () => {
		if (missingRequired.length > 0) return;
		const payload: OrderItemStatusFields = {};
		for (const field of fields) {
			if (metaFor(field).kind === 'date') {
				const d = dates[field];
				if (d) (payload as Record<string, string>)[field] = d.toISOString();
			} else {
				const v = text[field]?.trim();
				if (v) (payload as Record<string, string>)[field] = v;
			}
		}
		onSubmit(payload);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{action.label}</DialogTitle>
					<DialogDescription>
						{action.label} for “{productName}”. Enter the details below to continue.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{fields.map((field) => {
								const meta = metaFor(field);
								return (
									<Field
										key={field}
										label={meta.label}
										required={requiredSet.has(field)}
									>
										{meta.kind === 'date' ? (
											<DatePicker
												value={dates[field] ?? null}
												onChange={(d) =>
													setDates((prev) => ({
														...prev,
														[field]: d ?? null,
													}))
												}
											/>
										) : (
											<Input
												value={text[field] ?? ''}
												onChange={(e) =>
													setText((prev) => ({
														...prev,
														[field]: e.target.value,
													}))
												}
												placeholder={meta.placeholder}
											/>
										)}
									</Field>
								);
							})}
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={loading || missingRequired.length > 0}>
							{loading ? 'Saving…' : action.label}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
